import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { loadCompanyAccesses } from "../services/companyaccess.api";
import { getAllCompanies } from "../services/company.api";
import { getMaintenanceDrag } from "../services/maintenance.api";
import { useCompanyStore } from "../states/store/Company";
import { useAppStore } from "../states/store/";
import {
  awsPresignedUrls,
  getPurchaseOrders,
} from "../services/purchaseOrder.api";
import { getStockByProductId } from "../services/stock.api";
import { changeStatusPurchaseRequisition } from "../services/purchaseRequisition.api";
import { STATUS_ITEM } from "@/types/purchaseRequisition.type";
import { getAllSuppliers } from "../services/supplier.api";
import { useMemo } from "react";

export const CACHE_TIMES = {
  ONE_MINUTE: 1000 * 60,
  FIVE_MINUTES: 1000 * 60 * 5,
};

export const useGetCompanyAccesses = (email?: string) => {
  const hasUserAndCompanyAccesses = Boolean(
    !localStorage.getItem("companyAccess") && email
  );

  return useQuery({
    queryKey: ["companyAccess", email],
    queryFn: async () => {
      const result = await loadCompanyAccesses(email);
      if (!result.ok) throw new Error("Error get companyAccess");

      return result.data;
    },
    enabled: hasUserAndCompanyAccesses,
    staleTime: CACHE_TIMES.ONE_MINUTE,
    gcTime: CACHE_TIMES.FIVE_MINUTES,
  });
};

export const useGetCompanies = () => {
  return useQuery({
    queryKey: ["companies"],
    queryFn: async () => {
      const result = await getAllCompanies();
      if (!result.ok) throw new Error("Error get companies");

      return result.data;
    },
  });
};

export const useGetAllMaintenances = () => {
  const { selectedCompanies, selectedUf } = useCompanyStore((state) => ({
    selectedCompanies: state.selectedCompanies,
    selectedUf: state.selectedUf,
  }));
  const { filters } = useAppStore((state) => state.dashboard.maintenances);
  const companyIds = useMemo(
    () => selectedCompanies.map((c) => c.companyId).join(","),
    [selectedCompanies]
  );
  return useQuery({
    queryKey: ["maintenances-all", companyIds, selectedUf, filters.dateFilters],
    queryFn: async () => {
      const result = await getMaintenanceDrag({ ...filters.dateFilters });
      if (!result.ok) throw new Error("Error get all maintenaces");

      return result.data;
    },
  });
};

export const usePurchaseOrderCheck = () => {
  const queryClient = useQueryClient();

  const check = async (supplierId: string) => {
    return await queryClient.fetchQuery({
      queryKey: ["purchaseOrders", { supplierId, fields: "itens" }],
      queryFn: () => getPurchaseOrders({ supplierId, fields: "itens" }),
    });
  };

  return { check };
};

export const useGetStockProductDetails = (productId?: string) => {
  return useQuery({
    queryKey: ["stock-product-details", productId],
    queryFn: () => getStockByProductId(productId!).then((stock) => stock.data),
    enabled: !!productId,
  });
};

export const useRejectedPurchaseOrdem = () => {
  return useMutation({
    mutationFn: async ({
      requisitionId,
      status,
      reason,
    }: {
      requisitionId: string;
      status: STATUS_ITEM;
      reason: string;
    }) => {
      const response = await changeStatusPurchaseRequisition({
        requisitionId,
        reason,
        status,
      });
      if (!response.ok || !response.data) {
        throw new Error("Erro ao processar pedido de cancelamento");
      }
      return response.data;
    },
  });
};

export const useGetAllSuppliers = () => {
  return useQuery({
    queryKey: ["all-suppliers"],
    queryFn: async () => {
      const response = await getAllSuppliers();
      if (!response.ok) {
        throw new Error("Erro ao buscar fornecedores");
      }
      return response.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useAttachFilePresignedUrls = () => {
  return useMutation({
    mutationFn: async (files: File[]) => {
      const response = await awsPresignedUrls(files);
      if (!response.ok) {
        throw new Error("Erro ao enviar arquivo");
      }
      return response.data;
    },
  });
};
